import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Zap, Network, Server, ShieldCheck, Users, Award,
  ArrowUpRight, ArrowRight, CircuitBoard, Cable, Building2, CheckCircle2,
  MapPin, Factory, GraduationCap, Landmark, ShoppingBag, Home as HomeIcon, Hospital,
} from "lucide-react";
import Reveal from "../components/Reveal";
import CountUp from "../components/CountUp";
import { ClientMarquee, HeroBg } from "../components/Shared";
import { fetchStatsSupabase, fetchFeaturedProjectsSupabase } from "../lib/supabase";
import SEO from "../components/SEO";

const FEATURES = [
  { icon: Zap, title: "HT & LT Works", desc: "Government-licensed Class-I execution of high and low tension electrical works for industrial and commercial sites." },
  { icon: Network, title: "Networking & ELV", desc: "Structured cabling, CCTV, fire access control and public address systems integrated into a single dependable network." },
  { icon: CircuitBoard, title: "Interior, Area Development and Infrastructure Works", desc: "Completed many IT, Hospitality, Institutions and Industrial works" },
  { icon: ShieldCheck, title: "Quality & Safety", desc: "Routine testing, commissioning and as-built documentation jointly verified with the client before handover." },
  { icon: Users, title: "Professional Team", desc: "Dedicated teams for design, procurement, execution, maintenance & safety" },
  { icon: Award, title: "Turnkey Delivery", desc: "End-to-end coordination right from shop floor drawings, sample board, display at site, execution of our installation with proper safety measures till handing over." },
];

const SERVICES = [
  { badge: "HT", title: "HT Works", desc: "High tension electrical works (11 KV only), installation coordination, testing support and site-ready execution." },
  { badge: "LT", title: "LT Works", desc: "Low tension distribution, cabling, terminations and reliable power delivery.", copper: true },
  { badge: "NS", title: "Networking Systems (Passive)", desc: "Structured cabling and network infrastructure for operational facilities.", copper: true },
  { badge: "ELV", title: "ELV Systems", desc: "Fire alarms, public address (PA) systems, AV systems, CCTV and integrated low-voltage solutions for modern facilities." },
];

function sectorIcon(sector = "") {
  const s = sector.toLowerCase();
  if (s.includes("industrial")) return Factory;
  if (s.includes("educat")) return GraduationCap;
  if (s.includes("govern")) return Landmark;
  if (s.includes("it") || s.includes("data")) return Server;
  if (s.includes("showroom") || s.includes("mall") || s.includes("shopping")) return ShoppingBag;
  if (s.includes("resident") || s.includes("apart")) return HomeIcon;
  if (s.includes("commercial")) return Building2;
  if (s.includes("hospital") || s.includes("biotech")) return Hospital;
  return Zap;
}

export default function Home() {
  const [stats, setStats] = useState({ ongoing: 0, completed: 0, total: 0 });
  const [featuredProjects, setFeaturedProjects] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(true);

  useEffect(() => {
    fetchStatsSupabase().then(setStats).catch(() => {});
    fetchFeaturedProjectsSupabase()
      .then((data) => setFeaturedProjects(data.slice(0, 4)))
      .catch(() => {})
      .finally(() => setLoadingProjects(false));
  }, []);

  return (
    <>
      <SEO
        title="SUV Engineers | Electrical Contractors"
        description="SUV Engineers provides turnkey electrical contracting services across hotels, hospitals, commercial buildings, industrial facilities and infrastructure projects throughout India."
      />
      {/* HERO */}
      <section className="hero" data-testid="home-hero">
        <HeroBg />
        <div className="hero-content">
          <p className="eyebrow fade-up">HT &amp; LT • Substations • Networking • Turnkey</p>
          <h1 className="fade-up d1">
            Electrical Engineering and contracting built for <span className="accent">reliable</span> performance.
          </h1>
          <p className="hero-copy fade-up d2">
            SUV Engineers delivers end-to-end electrical infrastructure, substation systems, and
            ELV installations for facilities that demand dependable execution and long-term serviceability.
          </p>
          <div className="hero-actions fade-up d3">
            <Link to="/contact" className="btn btn-primary" data-testid="hero-quote-btn">
              Discuss a Project <ArrowUpRight size={18} />
            </Link>
            <Link to="/projects" className="btn btn-ghost" data-testid="hero-work-btn">
              View Our Work <ArrowRight size={18} />
            </Link>
          </div>
          <div className="hero-stats fade-up d4">
            <div className="hero-stat"><strong><CountUp end={stats.total || 200} suffix="+" /></strong><span>Projects Delivered</span></div>
            <div className="hero-stat"><strong><CountUp end={30} suffix="+" /></strong><span>Years Experience</span></div>
          </div>
        </div>
        <div className="hero-figure" aria-hidden="true">
          <div className="hf-card">
            <div className="hf-head"><span className="hf-dot" /> Systems Online</div>
            <div className="hf-bars">
              <div className="hf-bar"><span>HT</span><i style={{ "--w": "88%" }} /></div>
              <div className="hf-bar"><span>LT</span><i style={{ "--w": "72%" }} /></div>
              <div className="hf-bar"><span>MSS</span><i style={{ "--w": "94%" }} /></div>
              <div className="hf-bar"><span>NET</span><i style={{ "--w": "66%" }} /></div>
            </div>
            <div className="hf-foot">
              <div><div className="v">100%</div><div className="l">Load Tested</div></div>
              <div><div className="v">Class-I</div><div className="l">Licensed</div></div>
            </div>
          </div>
        </div>
      </section>

      <ClientMarquee />

      {/* WHY */}
      <section className="section why-section">
        <div className="section-inner">
          <Reveal className="section-head">
            <p className="eyebrow">Why SUV Engineers</p>
            <h2>Engineering depth with execution teams clients can trust.</h2>
            <p>
              Thorough professionals in electrical engineering across industrial, commercial, interior, area development
              and infrastructure projects — with additional ELV capabilities including fire access control,
              public address systems and CCTV.
            </p>
          </Reveal>
          <div className="feature-grid">
            {FEATURES.map((f, i) => (
              <Reveal key={f.title} className="feature-card" delay={i * 70}>
                <span className="feature-icon"><f.icon /></span>
                <h3>{f.title}</h3>
                <p>{f.desc}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES (dark) */}
      <section className="section dark">
        <div className="section-inner">
          <Reveal className="section-head">
            <p className="eyebrow">Core Services</p>
            <h2>Specialised electrical &amp; network infrastructure.</h2>
          </Reveal>
          <div className="service-grid">
            {SERVICES.map((s, i) => (
              <Reveal key={s.title} className={`service-card ${s.copper ? "copper" : ""}`} delay={i * 70}>
                <span className="service-badge">{s.badge}</span>
                <h3>{s.title}</h3>
                <p>{s.desc}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* STATS BAND */}
      <section className="section muted">
        <div className="section-inner">
          <div className="stats-band">
            {[
              { text: "Class-I", lbl: "Licensed Contractors" },
              { end: 30, suffix: "+", lbl: "Years of Experience" },
              { end: stats.ongoing || 11, suffix: "", lbl: "Ongoing Projects" },
              { text: "Many", lbl: "Projects Across Fields & Sectors", small: true },
            ].map((s, i) => (
              <Reveal key={s.lbl} className="stat-item" delay={i * 80}>
                <div className={`num${s.small ? " num--small" : ""}`}>{s.text ? s.text : <CountUp end={s.end} suffix={s.suffix} />}</div>
                <div className="lbl">{s.lbl}</div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED PROJECTS */}
      <section className="section">
        <div className="section-inner">
          <Reveal className="section-head">
            <p className="eyebrow">Highlights</p>
            <h2>Featured Projects</h2>
            <p>
              A selection of our major turnkey electrical works and installations delivered for leading industrial and commercial clients.
            </p>
          </Reveal>

          {loadingProjects ? (
            <div className="page-loader"><div className="spinner" /></div>
          ) : featuredProjects.length === 0 ? (
            <div className="empty-state">No featured projects found.</div>
          ) : (
            <div className="proj-list" data-testid="featured-projects-grid">
              {featuredProjects.map((p, i) => {
                const Icon = sectorIcon(p.sector);
                return (
                  <Reveal key={p.id} className={`proj-row ${p.status}`} delay={(i % 4) * 50}>
                    <span className="proj-ic"><Icon /></span>
                    <div className="proj-info">
                      <div className="pn">{p.name}</div>
                      <div className="pm">
                        <span className="sec">{p.sector}</span>
                        {p.location && <span className="loc"><MapPin /> {p.location}</span>}
                      </div>
                    </div>
                    <span className={`proj-dot ${p.status}`}>
                      <span className="d" />{p.status === "ongoing" ? "Ongoing" : "Completed"}
                    </span>
                  </Reveal>
                );
              })}
            </div>
          )}

          <div style={{ textAlign: "center", marginTop: 32 }}>
            <Link to="/projects" className="btn btn-primary" data-testid="home-view-all-projects-btn">
              View All Projects <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* SECTORS strip */}
      <section className="section">
        <div className="section-inner split-grid">
          <Reveal>
            <p className="eyebrow">Sectors We Serve</p>
            <h2 style={{ fontSize: "clamp(1.9rem,3.6vw,2.8rem)" }}>From factories to campuses — wired right.</h2>
          </Reveal>
          <Reveal className="content-stack" delay={120}>
            <p>
              We have successfully executed projects across IT parks, industrial plants, commercial
              complexes, base buildings, layouts and interiors — covering internal, external, LT and HT works.
            </p>
            <div className="pill-list">
              {["Industrial", "Commercial", "IT & Datacenters", "Educational", "Government", "Healthcare", "Hospitality", "Showrooms", "Residential"].map((p) => (
                <span key={p} className="pill"><CheckCircle2 size={14} style={{ marginRight: 6, verticalAlign: "-2px" }} />{p}</span>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* CTA */}
      <section className="section" style={{ paddingTop: 0 }}>
        <div className="section-inner">
          <Reveal className="cta-band">
            <div>
              <p className="eyebrow" style={{ color: "var(--amber)" }}>Start a Conversation</p>
              <h2>Share your site requirement and get a clear next step.</h2>
              <p>We personally review every enquiry to give you accurate scope and timelines.</p>
            </div>
            <Link to="/contact" className="btn btn-copper" data-testid="cta-contact-btn">
              Contact SUV Engineers <ArrowUpRight size={18} />
            </Link>
          </Reveal>
        </div>
      </section>
    </>
  );
}
